<template>
  <span class="provider-switcher-inline">
    <span 
      class="provider-toggle" 
      @click="toggleDropdown"
      :title="'Data Provider: ' + currentProvider.toUpperCase()"
    >
      <span class="provider-icon">{{ activeProviderIcon }}</span>
      <span class="provider-label">{{ currentProvider.toUpperCase() }}</span>
      <span :class="['status-dot', isAuthenticated ? 'connected' : 'disconnected']"></span>
      <span class="dropdown-arrow">▾</span>
    </span>
    
    <div v-if="showDropdown" class="provider-dropdown" @click.stop>
      <button 
        v-for="provider in visibleProviders" 
        :key="provider.name"
        @click="switchProvider(provider.name)"
        :class="['provider-option', { active: currentProvider === provider.name, disabled: isProviderDisabled(provider.name) }]"
        :disabled="isProviderDisabled(provider.name) || ((provider.name === 'aliceblue' && !isAuthenticated && currentProvider !== 'aliceblue') || (provider.name === 'fyers' && !isAuthenticated && currentProvider !== 'fyers') || (provider.name === 'upstox' && !isAuthenticated && currentProvider !== 'upstox'))"
        :title="isProviderDisabled(provider.name) ? getDisabledReason(provider.name) : ''"
      >
        <span class="option-icon">{{ provider.icon }}</span>
        <span class="option-details">
          <span class="option-title">{{ provider.title }}</span>
          <span class="option-desc" v-if="!isProviderDisabled(provider.name)">{{ provider.description }}</span>
          <span class="option-desc disabled-label" v-else>Disabled by admin</span>
        </span>
      </button>
    </div>

    <!-- AliceBlue Authentication Modal -->
    <div v-if="showAuthModal" class="modal-overlay" @click="showAuthModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>AliceBlue Account</h3>
          <button @click="showAuthModal = false" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <AliceBlueAuth 
            @authenticated="handleAuthenticated"
            @disconnected="handleDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- Fyers Authentication Modal -->
    <div v-if="showFyersAuthModal" class="modal-overlay" @click="showFyersAuthModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Fyers Account</h3>
          <button @click="showFyersAuthModal = false" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <FyersAuth 
            @authenticated="handleAuthenticated"
            @disconnected="handleDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- Upstox Authentication Modal -->
    <div v-if="showUpstoxAuthModal" class="modal-overlay" @click="showUpstoxAuthModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Upstox Account</h3>
          <button @click="showUpstoxAuthModal = false" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <UpstoxAuth 
            @authenticated="handleAuthenticated"
            @disconnected="handleDisconnected"
          />
        </div>
      </div>
    </div>

    <!-- DhanHQ Authentication Modal -->
    <div v-if="showDhanAuthModal" class="modal-overlay" @click="showDhanAuthModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>DhanHQ Account</h3>
          <button @click="showDhanAuthModal = false" class="close-button">&times;</button>
        </div>
        <div class="modal-body">
          <DhanAuth 
            @authenticated="handleAuthenticated"
            @disconnected="handleDisconnected"
          />
        </div>
      </div>
    </div>

  </span>
</template>

<script>
import DataProviderManager from '../services/data_provider_manager.js';
import AliceBlueAuth from './AliceBlueAuth.vue';
import FyersAuth from './FyersAuth.vue';
import UpstoxAuth from './UpstoxAuth.vue';
import DhanAuth from './DhanAuth.vue';

export default {
  name: 'DataProviderSwitcher',
  components: {
    AliceBlueAuth,
    FyersAuth,
    UpstoxAuth,
    DhanAuth
  },
  data() {
    return {
      currentProvider: 'binance',
      isAuthenticated: false,
      showAuthModal: false,
      showFyersAuthModal: false,
      showUpstoxAuthModal: false,
      showDhanAuthModal: false,
      showDropdown: false,
      disabledIntegrations: {},
      providers: [
        {
          name: 'binance',
          title: 'Binance',
          description: 'Crypto data',
          icon: '₿'
        },
        {
          name: 'fyers',
          title: 'Fyers',
          description: 'NSE / BSE / F&O',
          icon: '🔶'
        },
        {
          name: 'upstox',
          title: 'Upstox',
          description: 'NSE / BSE / MCX / F&O',
          icon: '🟣'
        },
        {
          name: 'dhan',
          title: 'DhanHQ',
          description: 'NSE / BSE / MCX / F&O',
          icon: '🔷',
          hidden: true // Disabled - kept for future use
        },
        {
          name: 'aliceblue',
          title: 'AliceBlue',
          description: 'Indian stocks',
          icon: '📈'
        },
        {
          name: 'coinbase',
          title: 'Coinbase',
          description: 'Crypto data',
          icon: '🔵'
        },
        {
          name: 'kraken',
          title: 'Kraken',
          description: 'Crypto data',
          icon: '🐙'
        },
        {
          name: 'bybit',
          title: 'Bybit',
          description: 'Crypto data',
          icon: '🟡'
        },
        {
          name: 'okx',
          title: 'OKX',
          description: 'Crypto data',
          icon: '⚫'
        },
        {
          name: 'amfi',
          title: 'AMFI',
          description: 'Indian mutual funds',
          icon: '🏦'
        }
      ]
    };
  },
  computed: {
    activeProviderIcon() {
      const p = this.providers.find(p => p.name === this.currentProvider);
      return p ? p.icon : '₿';
    },
    visibleProviders() {
      return this.providers.filter(p => !p.hidden);
    }
  },
  mounted() {
    this.initializeProvider();
    this.fetchIntegrationStatus();
    document.addEventListener('click', this.closeDropdown);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeDropdown);
  },
  methods: {
    initializeProvider() {
      DataProviderManager.init();
      this.updateProviderInfo();
      this.$root.$on('provider-changed', this.updateProviderInfo);
    },
    async fetchIntegrationStatus() {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_BASE || window.location.origin}/api/integrations/status`
        );
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            this.disabledIntegrations = data.integrations || {};
          }
        }
      } catch (error) {
        console.warn('[DataProviderSwitcher] Could not fetch integration status:', error.message);
      }
    },
    isProviderDisabled(providerName) {
      const setting = this.disabledIntegrations[providerName];
      return setting && !setting.enabled;
    },
    getDisabledReason(providerName) {
      const setting = this.disabledIntegrations[providerName];
      return setting && setting.disabledReason ? setting.disabledReason : 'Disabled by administrator';
    },
    updateProviderInfo() {
      this.currentProvider = DataProviderManager.getCurrentProvider();
      this.isAuthenticated = DataProviderManager.isAuthenticated();
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    closeDropdown() {
      this.showDropdown = false;
    },
    async switchProvider(providerName) {
      if (providerName === this.currentProvider) {
        this.showDropdown = false;
        return;
      }
      // Check if integration is disabled by admin
      if (this.isProviderDisabled(providerName)) {
        alert(`${providerName.charAt(0).toUpperCase() + providerName.slice(1)} is currently disabled by the administrator.${this.getDisabledReason(providerName) !== 'Disabled by administrator' ? '\nReason: ' + this.getDisabledReason(providerName) : ''}`);
        this.showDropdown = false;
        return;
      }
      if (providerName === 'aliceblue' && !DataProviderManager.providers.aliceblue.isAuthenticated()) {
        this.showAuthModal = true;
        this.showDropdown = false;
        return;
      }
      if (providerName === 'fyers' && !DataProviderManager.providers.fyers.isAuthenticated()) {
        this.showFyersAuthModal = true;
        this.showDropdown = false;
        return;
      }
      if (providerName === 'upstox' && !DataProviderManager.providers.upstox.isAuthenticated()) {
        this.showUpstoxAuthModal = true;
        this.showDropdown = false;
        return;
      }
      if (providerName === 'dhan' && !DataProviderManager.providers.dhan.isAuthenticated()) {
        this.showDhanAuthModal = true;
        this.showDropdown = false;
        return;
      }
      try {
        DataProviderManager.switchProvider(providerName);
        this.updateProviderInfo();
        this.showDropdown = false;
        this.$emit('provider-changed', {
          provider: providerName,
          info: DataProviderManager.getProviderInfo()
        });
      } catch (error) {
        console.error('Failed to switch provider:', error);
      }
    },
    handleAuthenticated(result) {
      this.updateProviderInfo();
      this.showAuthModal = false;
      this.showFyersAuthModal = false;
      this.showUpstoxAuthModal = false;
      this.showDhanAuthModal = false;
      this.$emit('provider-changed', {
        provider: DataProviderManager.getCurrentProvider(),
        info: DataProviderManager.getProviderInfo()
      });
    },
    handleDisconnected() {
      this.updateProviderInfo();
      if (['aliceblue', 'fyers', 'upstox', 'dhan'].includes(this.currentProvider)) {
        this.switchProvider('binance');
      }
    }
  }
};
</script>

<style scoped>
.provider-switcher-inline {
  position: relative;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

.provider-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #9b9ca0;
  user-select: none;
}

.provider-toggle:hover {
  background: rgba(130, 130, 130, 0.15);
}

.provider-icon {
  font-size: 14px;
}

.provider-label {
  font-weight: 500;
  font-size: 11px;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.connected {
  background: #4caf50;
}

.status-dot.disconnected {
  background: #f44336;
}

.dropdown-arrow {
  font-size: 10px;
  opacity: 0.6;
}

.provider-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 180px;
  padding: 4px;
}

.provider-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  text-align: left;
}

.provider-option:hover:not(:disabled) {
  background: #f0f2f5;
}

.provider-option.active {
  background: #e3f2fd;
}

.provider-option:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.option-icon {
  font-size: 16px;
}

.option-details {
  display: flex;
  flex-direction: column;
}

.option-title {
  font-size: 12px;
  font-weight: 600;
  color: #333;
}

.option-desc {
  font-size: 10px;
  color: #888;
}

.option-desc.disabled-label {
  color: #ef4444;
  font-weight: 500;
}

.provider-option.disabled {
  opacity: 0.4;
  background: #f8f8f8;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--tc-card-bg);
  color: var(--tc-text-primary);
  border: 1px solid var(--tc-card-border);
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--tc-border-primary);
}

.modal-header h3 {
  margin: 0;
  color: var(--tc-text-primary);
}

.close-button {
  background: none;
  border: none;
  color: var(--tc-text-secondary);
  font-size: 24px;
  cursor: pointer;
  transition: color 0.2s;
}

.close-button:hover {
  color: var(--tc-text-primary);
}

.modal-body {
  padding: 20px;
}
</style>
